/**
 * PatientToday — the calm, state-aware default screen for families and patients.
 *
 * Replaces the dense `PatientDashboard` at `/patient/dashboard`. The full
 * dashboard remains accessible at `/patient/dashboard/full` for power users
 * via the "Manage everything →" link in the corner.
 *
 * Design rule of three: at any given moment the user sees exactly ONE
 * primary "what's most relevant right now" card + at most three small
 * supporting cards underneath. No widget-grid. No equal-weight tiles.
 *
 * Primary state precedence (highest first):
 *   1. active_visit          — a visit is in progress NOW
 *   2. upcoming_visit_today  — a visit later today
 *   3. review_pending        — completed visit awaiting the patient's review
 *   4. upcoming_visit_soon   — a visit in the next 7 days
 *   5. cold_start            — no bookings yet → Find Care wizard
 *   6. default_returning     — returning user, nothing pressing
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { patientNavItems } from '../components/brand/navItems';
import { Button } from '../components/ui/button';
import {
  Sparkles, ArrowRight, MessageSquare, ShieldAlert, Calendar, Video, MapPin,
  Heart, Loader2, Star, Activity, CheckCircle2, Coffee, Sun, Moon,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ----------------------------------------------------------------------------
// Tiny helpers — kept inline so the file is self-contained.
// ----------------------------------------------------------------------------
const greeting = () => {
  const h = new Date().getHours();
  if (h < 5) return { word: 'Resting well', icon: Moon };
  if (h < 12) return { word: 'Good morning', icon: Coffee };
  if (h < 17) return { word: 'Good afternoon', icon: Sun };
  return { word: 'Good evening', icon: Moon };
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' }); }
  catch { return iso; }
};

const fmtTime = (timeStr) => {
  // backend stores HH:MM 24h. Render as 12h.
  if (!timeStr) return '';
  const [hh, mm] = timeStr.split(':');
  const h = parseInt(hh, 10);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${mm} ${suffix}`;
};

const isToday = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  const today = new Date();
  return d.getFullYear() === today.getFullYear()
    && d.getMonth() === today.getMonth()
    && d.getDate() === today.getDate();
};

const daysUntil = (iso) => {
  if (!iso) return Infinity;
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.floor((d.setHours(0, 0, 0, 0) - today.getTime()) / 86400000);
};

const clinicianName = (appt) => {
  const c = appt?.clinician || {};
  return [c.first_name, c.last_name].filter(Boolean).join(' ') || 'your clinician';
};

// ----------------------------------------------------------------------------
// State derivation — pure function, easy to test.
// ----------------------------------------------------------------------------
function derivePrimaryState({ appointments, reviewsEligible }) {
  const upcomingOrActive = appointments.filter((a) => !['cancelled'].includes(a.status));

  const active = upcomingOrActive.find((a) => ['in_progress', 'started'].includes(a.status));
  if (active) return { kind: 'active_visit', appointment: active };

  const todayVisit = upcomingOrActive.find((a) => isToday(a.scheduled_date) && ['confirmed', 'scheduled'].includes(a.status));
  if (todayVisit) return { kind: 'upcoming_visit_today', appointment: todayVisit };

  if (reviewsEligible.length > 0) {
    return { kind: 'review_pending', item: reviewsEligible[0] };
  }

  const upcoming = upcomingOrActive
    .filter((a) => daysUntil(a.scheduled_date) > 0 && daysUntil(a.scheduled_date) <= 14)
    .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date));
  if (upcoming[0]) return { kind: 'upcoming_visit_soon', appointment: upcoming[0] };

  if (appointments.length === 0) return { kind: 'cold_start' };
  return { kind: 'default_returning' };
}

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------
export default function PatientToday() {
  const { user, getAuthHeader, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);
  const [reviewsEligible, setReviewsEligible] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [apptRes, reviewsRes, msgRes] = await Promise.all([
        axios.get(`${API}/appointments`, getAuthHeader()).catch(() => ({ data: [] })),
        axios.get(`${API}/reviews/eligible`, getAuthHeader()).catch(() => ({ data: [] })),
        axios.get(`${API}/messages/unread-count`, getAuthHeader()).catch(() => ({ data: { count: 0 } })),
      ]);
      setAppointments(Array.isArray(apptRes.data) ? apptRes.data : (apptRes.data?.appointments || []));
      setReviewsEligible(Array.isArray(reviewsRes.data) ? reviewsRes.data : []);
      setUnreadMessages(msgRes.data?.count ?? msgRes.data?.unread_count ?? 0);
    } finally {
      setLoading(false);
    }
  }, [token, getAuthHeader]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const state = useMemo(
    () => derivePrimaryState({ appointments, reviewsEligible }),
    [appointments, reviewsEligible],
  );
  const greet = greeting();
  const GreetIcon = greet.icon;

  return (
    <DashboardShell
      navItems={patientNavItems({ pathname: location.pathname })}
      title={null}
      testId="patient-today"
    >
      {/* Greeting strip — quiet, low-density */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6" data-testid="today-greeting">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-cn-primary/10 text-cn-primary flex items-center justify-center">
            <GreetIcon className="w-5 h-5" />
          </div>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-cn-text dark:text-white">
              {greet.word}, {user?.first_name || 'friend'}.
            </h1>
            <p className="text-xs text-cn-text-muted">
              {fmtDate(new Date().toISOString())}
            </p>
          </div>
        </div>
        <Link
          to="/patient/dashboard/full"
          className="text-sm font-semibold text-cn-text-muted hover:text-cn-primary transition-colors"
          data-testid="today-manage-everything"
        >
          Manage everything &rarr;
        </Link>
      </div>

      {loading ? (
        <div className="cn-card p-16 text-center"><Loader2 className="w-7 h-7 text-cn-primary animate-spin mx-auto" /></div>
      ) : (
        <>
          <PrimaryCard state={state} navigate={navigate} token={token} onActed={fetchAll} />
          <SupportingCards unreadMessages={unreadMessages} primaryState={state.kind} />
        </>
      )}
    </DashboardShell>
  );
}

// ----------------------------------------------------------------------------
// Primary card — switches on state. Only ONE renders at a time.
// ----------------------------------------------------------------------------
function PrimaryCard({ state, navigate, token, onActed }) {
  switch (state.kind) {
    case 'active_visit':
      return <ActiveVisitCard appt={state.appointment} navigate={navigate} />;
    case 'upcoming_visit_today':
      return <UpcomingTodayCard appt={state.appointment} navigate={navigate} />;
    case 'review_pending':
      return <ReviewPendingCard item={state.item} token={token} onSubmitted={onActed} />;
    case 'upcoming_visit_soon':
      return <UpcomingSoonCard appt={state.appointment} navigate={navigate} />;
    case 'cold_start':
      return <ColdStartCard />;
    case 'default_returning':
    default:
      return <DefaultReturningCard />;
  }
}

// ---------- individual cards ----------

const cardClasses = 'rounded-3xl border border-cn-primary/20 bg-gradient-to-br from-cn-primary/8 via-cn-primary/5 to-transparent p-7 sm:p-10';

function ActiveVisitCard({ appt, navigate }) {
  return (
    <div className={cardClasses} data-testid="primary-active-visit">
      <p className="text-xs uppercase tracking-wider text-cn-primary font-bold mb-2 inline-flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-cn-primary animate-pulse" /> Care in progress
      </p>
      <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-cn-text dark:text-white">
        {clinicianName(appt)} is with you now
      </h2>
      <p className="text-cn-text-muted mt-2">
        {appt.appointment_type === 'telehealth' ? 'Telehealth visit' : 'In-home visit'} · started {fmtTime(appt.scheduled_time)}
      </p>
      <div className="flex flex-wrap gap-3 mt-7">
        {appt.appointment_type === 'telehealth' && (
          <Button onClick={() => navigate(`/telehealth/${appt.id}`)} className="cn-btn cn-btn-primary text-base" data-testid="active-visit-join-btn">
            <Video className="w-4 h-4" /> Open telehealth
          </Button>
        )}
        <Button onClick={() => navigate('/patient/messages')} className="cn-btn cn-btn-secondary text-base" data-testid="active-visit-message-btn">
          <MessageSquare className="w-4 h-4" /> Message {clinicianName(appt).split(' ')[0]}
        </Button>
      </div>
    </div>
  );
}

function UpcomingTodayCard({ appt, navigate }) {
  return (
    <div className={cardClasses} data-testid="primary-upcoming-today">
      <p className="text-xs uppercase tracking-wider text-cn-primary font-bold mb-2">Today</p>
      <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-cn-text dark:text-white">
        {clinicianName(appt)} arrives at {fmtTime(appt.scheduled_time)}
      </h2>
      <p className="text-cn-text-muted mt-2 flex items-center gap-2">
        {appt.appointment_type === 'telehealth'
          ? <><Video className="w-4 h-4" /> Telehealth visit · {appt.duration_minutes} min</>
          : <><MapPin className="w-4 h-4" /> In-home visit · {appt.duration_minutes} min</>}
      </p>
      <div className="flex flex-wrap gap-3 mt-7">
        <Button onClick={() => navigate('/patient/messages')} className="cn-btn cn-btn-primary text-base" data-testid="today-message-btn">
          <MessageSquare className="w-4 h-4" /> Anything {clinicianName(appt).split(' ')[0]} should know?
        </Button>
        <Link to="/patient/offers" className="cn-btn cn-btn-ghost text-base" data-testid="today-view-booking-btn">
          View booking details
        </Link>
      </div>
    </div>
  );
}

function UpcomingSoonCard({ appt, navigate }) {
  const days = daysUntil(appt.scheduled_date);
  const whenLabel = days === 1 ? 'tomorrow' : `in ${days} days`;
  return (
    <div className={cardClasses} data-testid="primary-upcoming-soon">
      <p className="text-xs uppercase tracking-wider text-cn-primary font-bold mb-2">Coming up</p>
      <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-cn-text dark:text-white">
        Your next visit is {whenLabel}
      </h2>
      <p className="text-cn-text-muted mt-2 flex items-center gap-2">
        <Calendar className="w-4 h-4" />
        {fmtDate(appt.scheduled_date)} at {fmtTime(appt.scheduled_time)} · with {clinicianName(appt)}
      </p>
      <div className="flex flex-wrap gap-3 mt-7">
        <Button onClick={() => navigate('/patient/messages')} className="cn-btn cn-btn-primary text-base" data-testid="soon-message-btn">
          <MessageSquare className="w-4 h-4" /> Message {clinicianName(appt).split(' ')[0]}
        </Button>
        <Link to="/patient/offers" className="cn-btn cn-btn-ghost text-base" data-testid="soon-reschedule-btn">
          Reschedule or cancel
        </Link>
      </div>
    </div>
  );
}

function ReviewPendingCard({ item, token, onSubmitted }) {
  const [rating, setRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (rating < 1) { toast.error('Pick at least one star'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews/booking`,
        { booking_id: item.booking_id, rating },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Thanks for the feedback');
      onSubmitted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit');
      setSubmitting(false);
    }
  };
  return (
    <div className={cardClasses} data-testid="primary-review-pending">
      <p className="text-xs uppercase tracking-wider text-cn-primary font-bold mb-2">Recent visit</p>
      <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-cn-text dark:text-white">
        How did {item.clinician_name || 'your clinician'} do?
      </h2>
      <p className="text-cn-text-muted mt-2">
        Your feedback helps other families pick well — and helps {(item.clinician_name || 'your clinician').split(' ')[0]} grow their practice.
      </p>
      <div className="flex items-center gap-1 mt-6" data-testid="review-pending-stars">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setRating(n)}
            className="p-1 hover:scale-110 transition-transform"
            data-testid={`review-pending-star-${n}`}
            aria-label={`${n} star${n > 1 ? 's' : ''}`}
          >
            <Star className={`w-8 h-8 ${n <= rating ? 'fill-amber-400 stroke-amber-500' : 'fill-none stroke-cn-text-muted'}`} />
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 mt-6">
        <Button onClick={submit} disabled={submitting || rating < 1} className="cn-btn cn-btn-primary text-base" data-testid="review-pending-submit">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Send rating
        </Button>
        <Link to="/patient/dashboard/full" className="cn-btn cn-btn-ghost text-base" data-testid="review-pending-detailed">
          Add a written review &rarr;
        </Link>
      </div>
    </div>
  );
}

function ColdStartCard() {
  return (
    <div className={cardClasses} data-testid="primary-cold-start">
      <p className="text-xs uppercase tracking-wider text-cn-primary font-bold mb-2 inline-flex items-center gap-1.5">
        <Sparkles className="w-4 h-4" /> Let&apos;s find the right care
      </p>
      <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-cn-text dark:text-white">
        Three soft questions and we&apos;ll show you the right clinicians.
      </h2>
      <p className="text-cn-text-muted mt-3 max-w-2xl">
        What kind of care, when, and where. You can change anything later — and every clinician we show you is license-verified and settlement-protected.
      </p>
      <div className="flex flex-wrap gap-3 mt-7">
        <Link to="/patient/find-care" className="cn-btn cn-btn-primary text-base" data-testid="cold-start-find-care-btn">
          Find care now <ArrowRight className="w-4 h-4" />
        </Link>
        <Link to="/patient/find-clinicians" className="cn-btn cn-btn-ghost text-base" data-testid="cold-start-browse-btn">
          Just browse clinicians
        </Link>
      </div>
    </div>
  );
}

function DefaultReturningCard() {
  return (
    <div className={cardClasses} data-testid="primary-default-returning">
      <p className="text-xs uppercase tracking-wider text-cn-primary font-bold mb-2">All caught up</p>
      <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-cn-text dark:text-white">
        What can we help with today?
      </h2>
      <p className="text-cn-text-muted mt-3 max-w-xl">
        Nothing urgent. You can find a new kind of care, message your team, or check past visits.
      </p>
      <div className="flex flex-wrap gap-3 mt-7">
        <Link to="/patient/find-care" className="cn-btn cn-btn-primary text-base" data-testid="default-find-care-btn">
          Find care <ArrowRight className="w-4 h-4" />
        </Link>
        <Link to="/patient/dashboard/full" className="cn-btn cn-btn-ghost text-base" data-testid="default-manage-btn">
          Manage everything
        </Link>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Supporting tiles — ALWAYS at most 3, smaller, calm.
// We deliberately drop the duplicate "Find Care" tile when the primary
// state already offers Find Care so we don't double-CTA.
// ----------------------------------------------------------------------------
function SupportingCards({ unreadMessages, primaryState }) {
  const showFindCareTile = !['cold_start', 'default_returning'].includes(primaryState);
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6" data-testid="today-supporting">
      <SupportingTile
        to="/patient/messages"
        icon={MessageSquare}
        title="Messages"
        subtitle={unreadMessages > 0 ? `${unreadMessages} unread` : 'Talk to your care team'}
        accent={unreadMessages > 0}
        testid="supporting-messages"
      />
      {showFindCareTile && (
        <SupportingTile
          to="/patient/find-care"
          icon={Sparkles}
          title="Find care"
          subtitle="Three soft questions"
          testid="supporting-find-care"
        />
      )}
      <SupportingTile
        to="tel:911"
        icon={ShieldAlert}
        title="Need help now?"
        subtitle="Call 911 — we never replace emergency care"
        emergency
        testid="supporting-emergency"
      />
      {!showFindCareTile && (
        <SupportingTile
          to="/patient/checkin"
          icon={Heart}
          title="Daily check-in"
          subtitle="How are you feeling today?"
          testid="supporting-checkin"
        />
      )}
    </div>
  );
}

function SupportingTile({ to, icon: Icon, title, subtitle, accent, emergency, testid }) {
  const tone = emergency
    ? 'border-red-300 bg-red-50 dark:bg-red-950/30 hover:border-red-400 text-red-700'
    : accent
      ? 'border-cn-primary/30 bg-cn-primary/[0.04] hover:border-cn-primary/50'
      : 'border-cn-border bg-white dark:bg-cn-ink-800 hover:border-cn-primary/40';
  return (
    <Link to={to} className={`rounded-3xl border-2 p-5 flex items-center gap-4 transition-all ${tone}`} data-testid={testid}>
      <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${
        emergency ? 'bg-red-100 text-red-700' : 'bg-cn-primary/10 text-cn-primary'
      }`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="font-semibold text-sm text-cn-text dark:text-white">{title}</p>
        <p className="text-xs text-cn-text-muted mt-0.5 truncate">{subtitle}</p>
      </div>
    </Link>
  );
}
